import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User, Sparkles, ExternalLink, Minimize2 } from 'lucide-react';

// Recipe Labs knowledge base for the support bot
const RECIPE_LABS_KNOWLEDGE = `
You are the Recipe Labs AI Support Assistant. You help team members use our tools effectively.

## About Recipe Labs
Recipe Labs is a suite of AI-powered tools for marketing, sales, and business operations.

## Our Tools

### AI Toolbox (recipelabs.madebyrecipe.com)
- 16+ AI assistants for writing, research, and productivity
- Use for: content creation, research, brainstorming, copywriting
- Features: Multiple AI models, templates, saved prompts

### Find New Clients (leadgen.madebyrecipe.com)
- Lead generation and prospecting tool
- Use for: finding potential customers, contact discovery, sales research
- Features: Company search, contact finder, export to CRM

### Email Writer (emailbuilder.madebyrecipe.com)
- AI-powered email composition
- Use for: professional emails, cold outreach, follow-ups, newsletters
- Features: Templates, tone adjustment, subject line generator

### Social Media Research (scraper.madebyrecipe.com)
- Social media monitoring and analysis
- Use for: trend research, competitor analysis, audience insights
- Features: Multi-platform search, sentiment analysis, export data

### Strategy Builder (playbook.madebyrecipe.com)
- Create guides and action plans
- Use for: SOPs, playbooks, onboarding docs, project plans
- Features: AI-generated outlines, step-by-step guides, templates

### Help & Tutorials (guide.madebyrecipe.com)
- Documentation and learning resources
- Use for: learning tools, troubleshooting, best practices
- Features: Video tutorials, FAQ, step-by-step guides

## Important Guidelines
1. NEVER share API keys, passwords, or credentials
2. NEVER share internal business data or client information
3. Direct users to guide.madebyrecipe.com for detailed documentation
4. Be helpful, concise, and professional
5. If you don't know something, say so and suggest checking documentation

## Common Questions You Can Help With
- How to use specific tools
- Best practices for each tool
- Troubleshooting common issues
- Feature recommendations
- Workflow suggestions
`;

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const GEMINI_API_KEY = 'AIzaSyDhhzVXv1J2hKPU8aC_mN8H2RIsz9IA6pY';

export default function SupportChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Add welcome message when chat opens for the first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: "Hey! I'm your Recipe Labs assistant. I can help you with any of our tools - just ask! What would you like to know?",
        timestamp: new Date()
      }]);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Build conversation history for context
      const conversationHistory = messages.map(msg => ({
        role: msg.role === 'assistant' ? 'model' : 'user',
        parts: [{ text: msg.content }]
      }));

      // Add current message
      conversationHistory.push({
        role: 'user',
        parts: [{ text: input.trim() }]
      });

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${GEMINI_API_KEY}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: conversationHistory,
            systemInstruction: {
              parts: [{ text: RECIPE_LABS_KNOWLEDGE }]
            },
            generationConfig: {
              temperature: 0.7,
              topK: 40,
              topP: 0.95,
              maxOutputTokens: 1024
            }
          })
        }
      );

      const data = await response.json();
      const assistantContent = data.candidates?.[0]?.content?.parts?.[0]?.text ||
        "I'm having trouble responding right now. Please try again or check guide.madebyrecipe.com for help.";

      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: assistantContent,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: "Sorry, I couldn't connect. Please try again in a moment.",
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickActions = [
    { label: 'How do I use AI Toolbox?', action: () => setInput('How do I use AI Toolbox?') },
    { label: 'Help with lead generation', action: () => setInput('How can I find new clients using the lead generation tool?') },
    { label: 'Writing better emails', action: () => setInput('What are the best practices for using Email Writer?') },
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-recipe-accent text-white rounded-full shadow-lg hover:bg-recipe-accent/90 transition-all duration-300 hover:scale-105 group"
        style={{
          boxShadow: '0 4px 20px rgba(200, 180, 100, 0.4)',
          animation: 'pulse-glow 2s ease-in-out infinite'
        }}
      >
        <Bot className="w-5 h-5" />
        <span className="font-medium">Need Help?</span>
        <Sparkles className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
        isMinimized ? 'w-72' : 'w-96'
      }`}
    >
      {/* Chat Container */}
      <div
        className="bg-[#0d1210] border border-recipe-border rounded-2xl shadow-2xl overflow-hidden"
        style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b border-recipe-border"
          style={{ background: 'linear-gradient(135deg, rgba(200, 180, 100, 0.1), transparent)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-recipe-accent/20 flex items-center justify-center">
              <Bot className="w-5 h-5 text-recipe-accent" />
            </div>
            <div>
              <h3 className="font-semibold text-recipe-text font-display text-sm">Recipe Labs Support</h3>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-xs text-recipe-text-muted">AI-Powered Assistant</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 hover:bg-recipe-accent/10 rounded-lg transition-colors"
              title={isMinimized ? 'Expand' : 'Minimize'}
            >
              <Minimize2 className="w-4 h-4 text-recipe-text-muted" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-recipe-accent/10 rounded-lg transition-colors"
              title="Close chat"
            >
              <X className="w-4 h-4 text-recipe-text-muted" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        {!isMinimized && (
          <>
            <div className="h-80 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-recipe-border scrollbar-track-transparent">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message.role === 'assistant'
                      ? 'bg-recipe-accent/20'
                      : 'bg-blue-500/20'
                  }`}>
                    {message.role === 'assistant' ? (
                      <Bot className="w-4 h-4 text-recipe-accent" />
                    ) : (
                      <User className="w-4 h-4 text-blue-400" />
                    )}
                  </div>
                  <div className={`max-w-[75%] ${message.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`px-3 py-2 rounded-2xl text-sm ${
                      message.role === 'assistant'
                        ? 'bg-recipe-card border border-recipe-border text-recipe-text rounded-tl-sm'
                        : 'bg-recipe-accent text-white rounded-tr-sm'
                    }`}>
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>
                    <p className="text-xs text-recipe-text-muted mt-1 px-1">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-recipe-accent/20 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-recipe-accent" />
                  </div>
                  <div className="px-4 py-3 bg-recipe-card border border-recipe-border rounded-2xl rounded-tl-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-recipe-accent animate-spin" />
                      <span className="text-sm text-recipe-text-muted">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions - Only show when no messages */}
            {messages.length <= 1 && (
              <div className="px-4 pb-2">
                <p className="text-xs text-recipe-text-muted mb-2">Quick questions:</p>
                <div className="flex flex-wrap gap-2">
                  {quickActions.map((action, i) => (
                    <button
                      key={i}
                      onClick={action.action}
                      className="px-3 py-1.5 text-xs bg-recipe-card border border-recipe-border rounded-full hover:border-recipe-accent transition-colors text-recipe-text-muted hover:text-recipe-text"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="p-3 border-t border-recipe-border">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything about our tools..."
                  className="flex-1 px-4 py-2.5 bg-recipe-bg border border-recipe-border rounded-xl text-sm text-recipe-text placeholder-recipe-text-muted focus:outline-none focus:ring-2 focus:ring-recipe-accent/50 focus:border-recipe-accent"
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  className="p-2.5 bg-recipe-accent text-white rounded-xl hover:bg-recipe-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center justify-between mt-2 px-1">
                <span className="text-xs text-recipe-text-muted/60">
                  Powered by Gemini AI
                </span>
                <a
                  href="https://guide.madebyrecipe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-recipe-accent hover:underline"
                >
                  <ExternalLink className="w-3 h-3" />
                  Full Documentation
                </a>
              </div>
            </div>
          </>
        )}
      </div>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 4px 20px rgba(200, 180, 100, 0.4); }
          50% { box-shadow: 0 4px 30px rgba(200, 180, 100, 0.6); }
        }
      `}</style>
    </div>
  );
}
